<template>
  <div
    id="app"
    class="h-100"
  >
    <component
      :is="layout"
      :key="key"
    >
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'
import {
  setUserLoginHook,
  setUserLogoutHook,
} from '@/../GjirafaFront/script/auth/config'

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import ability from '@/libs/acl/ability'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    // Layouts
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return 'layout-vertical'
    },
    key() {
      return this.$store.state.app.key
    },
    projectId() {
      return this.$store.state.app.projectId
    },
    organizationId() {
      return this.$store.state.app.organizationId
    },
    user() {
      return this.$store.state.users.user
    },
    setDefaultUser() {
      return this.$store.state.app.setDefaultUser
    },
  },
  watch: {
    projectId() {
      if (this.projectId) {
        this.fetchPermissions()
        this.fetchServices()
        if (this.$route.name === 'organization') {
          this.$router.push({ name: 'distributions', query: this.$route.query }).catch(() => {})
        }
      }
      this.$store.commit(
        'appConfig/UPDATE_NAV_MENU_HIDDEN',
        this.projectId === null || this.organizationId === null,
      )
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      'primary',
      'secondary',
      'success',
      'info',
      'warning',
      'danger',
      'light',
      'dark',
    ]

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement,
      ).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement,
        ).value.slice(0, -2),
      )
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')

    setUserLoginHook(async userDataObject => {
      if (this.setDefaultUser) {
        await this.$store
          .dispatch('users/fetchUser')
          .then(() => {
            if (this.user.defaultOrganizationId) {
              this.$store.commit(
                'app/SET_ORGANIZATION_ID',
                this.user.defaultOrganizationId,
              )
            }
            if (this.user.defaultProjectId) {
              this.$store.commit(
                'app/SET_PROJECT_ID',
                this.user.defaultProjectId,
              )
            }
          })
          .catch(() => this.$router.push({ name: 'organization', query: this.$route.query }))
      }

      return userDataObject
    })
    // setUserLogoutHook
    setUserLogoutHook(() => {
      localStorage.removeItem('projectPage')
      localStorage.removeItem('userData')
      this.$store.commit('app/TOGGLE_LOGGING_OUT', false)
    })
  },
  setup() {
    const { enableScrollToTop } = $themeConfig.layout

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    const showMenu = store.state.app.projectId === null
    store.commit('appConfig/UPDATE_NAV_MENU_HIDDEN', showMenu)

    return {
      enableScrollToTop,
    }
  },
  methods: {
    fetchPermissions() {
      this.$store
        .dispatch('permissions/fetchPermissions')
        .then(() => {
          const permissionsOfRole = store.getters['permissions/getPermissions']
          ability.update(permissionsOfRole)
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching permissions.',
          )
        })
    },
    fetchServices() {
      this.$store.dispatch('captianServices/fetchServices').catch(() => {})
    },
  },
}
</script>
